import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import {
    IconButton,
    ProgressIndicator,
    Stack
} from "@fluentui/react";
import React from "react";
import { CloseButtonProps } from "../../../../shared/props";
import { Image } from "../../../Image/Image";
import { ImageProps } from "../../../Image/Image.types";
import { RichText, RichTextProps } from "../../../RichText/RichText";
import { SearchBox, SearchBoxProps } from "../../../SearchBox/SearchBox";
import { TruncatedText } from "../../../TruncatedText/TruncatedText";
import {
    getHeaderContentStyles,
    getIconButtonStyles,
    getIconPlaceholderStyles,
    progressBarStyles
} from "../../Dialog.styles";
import { BackButtonProps, DialogHeaderStyle } from "../../Dialog.types";

export interface DialogHeaderOperation {
    /**
     * Operation data for progress view
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Title of dialog when operation failed.
     */
    readonly errorDialogTitle?: string | ImageProps | ImageWithTextProps;

    /**
     * Title of dialog when operation success.
     */
    readonly successDialogTitle?: string | ImageProps | ImageWithTextProps;

    /**
     * Title of dialog when operation in progress.
     */
    readonly progressDialogTitle?: string | ImageProps | ImageWithTextProps;
}

export interface DialogHeaderProps {
    /**
     * Contains operation data.
     */
    readonly operationData?: DialogHeaderOperation;
    /**
     * Contains the title.
     */
    readonly title?: string | ImageProps | ImageWithTextProps;

    /**
     * The ID of the title element
     */
    readonly titleAriaId?: string;
    /**
     * Contains the searchBox.
     */
    readonly searchBoxProps?: SearchBoxProps;
    /**
     * Contains the subtitle.
     */
    readonly subtitle?: RichTextProps;
    /**
     * Optional override for left and right paddings to maintain alignment with content in the body
     */
    readonly headerLeftRightPadding?: "16px" | "24px";
    /**
     * Optional override for headingTag to set the heading level for the title
     */
    readonly headingTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
    /**
     * Optional close button for the dialog.
     */
    readonly closeButton?: CloseButtonProps;
    /**
     * A callback function which is called after the Modal is dismissed and the animation is complete.
     */
    readonly onCloseButtonClicked?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    /**
     * Optional back button for the dialog.
     */
    readonly backButton?: BackButtonProps;
    /**
     * Optional overrride for adding a horizontal line in the header
     */
    readonly withHeaderHorizontalLine?: boolean;

}

export interface ImageWithTextProps {

    /**
     * Contains the title text
     */
    readonly text: string;

    /**
     * Contains the title image
     */
    readonly image: ImageProps;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
    title,
    titleAriaId,
    searchBoxProps,
    subtitle,
    closeButton,
    backButton,
    headerLeftRightPadding,
    headingTag = "h1",
    operationData,
    withHeaderHorizontalLine,
}) => {
    const theme = useTheme();
    const dialogHeaderStyle: DialogHeaderStyle = { headerLeftRightPadding: headerLeftRightPadding || "24px", withHeaderHorizontalLine };
    const headerStyleSets = getHeaderContentStyles(theme, dialogHeaderStyle);
    // Default height as per figma.
    const titleImageHeight = 24;
    // children gap from dialog title depends on the element as per figma.
    // tslint:disable-next-line: no-magic-numbers
    const calculatedChildrenGap = searchBoxProps ? 6 : (subtitle ? 8 : 0);

    const dialogHeader = (dialogTitle?: string | ImageProps | ImageWithTextProps) => {
        let titleElement = <></>;

        if (!dialogTitle && !closeButton) {
            return titleElement;
        }
        if (dialogTitle) {
            if (typeof dialogTitle === "string") {
                titleElement = (
                    <TruncatedText
                        type="heading"
                        tag={headingTag}
                        variant="paneHeader"
                        id={titleAriaId}
                        lines={2}
                    >
                        {dialogTitle}
                    </TruncatedText>
                );
            } else if ("src" in dialogTitle) {
                titleElement = (
                    <Image
                        {...dialogTitle}
                        id={titleAriaId}
                        height={titleImageHeight}
                    />
                );
            } else {
                titleElement = (
                    <Stack horizontal={true} tokens={{ childrenGap: "8px" }} verticalAlign="center">
                        <Image
                            {...dialogTitle.image}
                            id={titleAriaId}
                            height={titleImageHeight}
                        />
                        <TruncatedText
                            type="heading"
                            tag={headingTag}
                            variant="paneHeader"
                            lines={2}
                        >
                            {dialogTitle.text}
                        </TruncatedText>
                    </Stack>
                );
            }
        }

        return (
            <Stack
                className={headerStyleSets}
                tokens={{
                    childrenGap: calculatedChildrenGap
                }}
            >
                <Stack
                    horizontal={true}
                    horizontalAlign="space-between"
                    styles={{ root: { minHeight: "32px" } }}
                >
                    {backButton ? (
                        <Stack
                            horizontal={true}
                            horizontalAlign="start"
                            tokens={{ childrenGap: "16px" }}
                        >
                            <IconButton
                                styles={getIconButtonStyles(theme, true)}
                                iconProps={{
                                    iconName: "Back"
                                }}
                                {...backButton}
                                ariaLabel={backButton["aria-label"]}
                            />
                            {titleElement}
                        </Stack>
                    ) : titleElement}
                    {closeButton && (<div style={getIconPlaceholderStyles()} />)}
                </Stack>
                <Stack
                    tokens={{
                        childrenGap: 16
                    }}
                >
                    {searchBoxProps && <SearchBox {...searchBoxProps} />}
                    {subtitle && <RichText {...subtitle} />}
                </Stack>
            </Stack>
        );
    };

    const defaultState = dialogHeader(title);

    if (operationData) {

        const progressState = (
            <>
                <div style={{ position: "absolute", width: "100%", zIndex: 1 }} aria-hidden={true}>
                    <ProgressIndicator styles={progressBarStyles} />
                </div>
                {operationData.progressDialogTitle ? dialogHeader(operationData.progressDialogTitle) : defaultState}
            </>
        );

        const errorState = dialogHeader(operationData.errorDialogTitle);

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                inertView={(defaultState)}
                firstLoadView={(progressState)}
                updatingView={(progressState)}
                updateFailedView={(errorState)}
                finishedView={(
                    <>
                        {operationData.successDialogTitle ? dialogHeader(operationData.successDialogTitle) : defaultState}
                    </>
                )}
                failedView={(errorState)}
            />
        );
    } else {
        return defaultState;
    }
};

export const DialogCloseButton: React.FC<Pick<DialogHeaderProps, "closeButton" | "onCloseButtonClicked" | "headerLeftRightPadding">> = ({
    closeButton,
    onCloseButtonClicked,
    headerLeftRightPadding = "24px"
}) => {
    const theme = useTheme();
    const dialogHeaderStyle: DialogHeaderStyle = { headerLeftRightPadding: headerLeftRightPadding, withHeaderHorizontalLine: false };
    const headerStyleSets = getHeaderContentStyles(theme, dialogHeaderStyle);

    const closeClicked = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (onCloseButtonClicked && event) {
            onCloseButtonClicked(event);
        }
    };

    return closeButton ? (
        <Stack
            className={headerStyleSets}
            styles={{
                root: {
                    position: "absolute",
                    top: 0,
                    right: 0
                }
            }}
        >
            <IconButton
                styles={getIconButtonStyles(theme)}
                iconProps={{
                    iconName: "Cancel"
                }}
                onClick={closeClicked}
                {...closeButton}
                ariaLabel={closeButton["aria-label"]}
            />
        </Stack>
    ) : null;
};
